import { useEffect } from 'react';
import { Pencil } from 'lucide-react';

import { Badge, Card, CardContent, Switch, Slider, Button } from '@design-system';
import { formatCurrency } from '@shared/formatCurrency';

import {
  useAllocation,
  useIsSourceEnabled,
  useActiveQuoteId,
  useRemainingBudget,
  useIsDisabled,
  getFundingAllocationActions,
} from '@/features/guided-financial-planning/stores';

export interface FundingSourceCardProps {
  selfFundingSourceId: string;
  selfFundingSource: SelfFundingSource;
  onEdit?: (selfFundingSourceId: string, selfFundingSource: SelfFundingSource) => void;
  sharedByPartnerName?: string | null;
  effectiveBalance?: number | null;
}

export function FundingSourceCard({
  selfFundingSourceId,
  selfFundingSource,
  onEdit,
  sharedByPartnerName,
  effectiveBalance,
}: FundingSourceCardProps) {
  const quoteId = useActiveQuoteId();
  const remainingBudget = useRemainingBudget();
  const disabled = useIsDisabled();

  const allocation = useAllocation(quoteId ?? '', selfFundingSourceId);
  const isEnabled = useIsSourceEnabled(quoteId ?? '', selfFundingSourceId);
  const { setAllocation, removeAllocation } = getFundingAllocationActions();

  const availableBalance = effectiveBalance ?? selfFundingSource.existingAssetsValue ?? 0;
  const maxAllowedValue = allocation + remainingBudget;
  const accountName =
    selfFundingSource.selfFundingName ?? selfFundingSource.accountType ?? 'Unknown Account';

  // Clamp allocation when available balance decreases below current allocation
  useEffect(() => {
    if (quoteId && isEnabled && allocation > availableBalance) {
      setAllocation(quoteId, selfFundingSourceId, availableBalance);
    }
  }, [quoteId, isEnabled, allocation, availableBalance, selfFundingSourceId, setAllocation]);

  const handleToggle = (checked: boolean) => {
    if (!quoteId) return;
    if (checked) {
      setAllocation(quoteId, selfFundingSourceId, 0);
    } else {
      removeAllocation(quoteId, selfFundingSourceId);
    }
  };

  const handleHeaderClick = () => {
    if (disabled) return;
    handleToggle(!isEnabled);
  };

  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleSliderChange = (values: number[]) => {
    if (!quoteId) return;
    const cappedValue = Math.min(values[0] ?? 0, maxAllowedValue);
    setAllocation(quoteId, selfFundingSourceId, cappedValue);
  };

  if (!quoteId) {
    return null;
  }

  return (
    <Card>
      <CardContent className="flex flex-col px-5 py-2">
        <button
          type="button"
          onClick={handleHeaderClick}
          disabled={disabled}
          className="flex w-full items-center justify-between text-left disabled:cursor-not-allowed disabled:opacity-60"
        >
          <div className="flex flex-col gap-0.5">
            <div className="flex items-center gap-2">
              <h3 className="text-base font-semibold text-gray-900">{accountName}</h3>
              {sharedByPartnerName ? (
                <Badge variant="default" className="text-xs">
                  Shared by {sharedByPartnerName}
                </Badge>
              ) : (
                onEdit && !disabled && (
                  <Button
                    type="button"
                    size="icon-sm"
                    variant="ghost"
                    className="size-7"
                    onClick={event => {
                      event.stopPropagation();
                      onEdit(selfFundingSourceId, selfFundingSource);
                    }}
                    aria-label={`Edit ${accountName}`}
                  >
                    <Pencil className="size-3.5" />
                  </Button>
                )
              )}
            </div>
            <p className="text-sm text-gray-700">Available: {formatCurrency(availableBalance)}</p>
          </div>
          <div
            onClick={handleSwitchClick}
            className="pointer-events-auto"
          >
            <Switch
              checked={isEnabled}
              onCheckedChange={handleToggle}
              disabled={disabled}
            />
          </div>
        </button>

        {isEnabled && (
          <div className="mt-4 space-y-3 border-t border-gray-200 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-gray-900">Contribution from this account</p>
              <p className="text-sm font-medium text-gray-900">
                {formatCurrency(allocation)}
                <span className="text-gray-400"> / </span>
                <span className="text-gray-500">{formatCurrency(availableBalance)}</span>
              </p>
            </div>
            <Slider
              value={[allocation]}
              onValueChange={handleSliderChange}
              min={0}
              max={availableBalance}
              step={100}
              disabled={disabled}
              className="**:data-[slot='slider-range']:bg-primary **:data-[slot='slider-track']:bg-gray-100"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
